import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { FilterButton } from "../atoms/FilterButton";
import { DropdownContent } from "../atoms/DropdownContent";
import { MultiSelectOption, SingleSelectOption } from "../atoms/DropdownOption";
import { Input } from "../ui/input";

interface FilterOption {
  value: string;
  label: string;
}

interface FilterFieldProps {
  label: string;
  type: "multiselect" | "singleselect" | "input" | "daysOpenSlider";
  options?: FilterOption[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  placeholder?: string;
  inputType?: "text" | "number";
  width?: "sm" | "md" | "lg";
  min?: string;
}

export function FilterField({
  label,
  type,
  options = [],
  value,
  onChange,
  placeholder,
  inputType = "text",
  width = "sm",
  min
}: FilterFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside.
  // Uses composedPath() to correctly handle clicks inside Shadow DOM —
  // e.target is retargeted to the shadow host in the outer document,
  // but composedPath() preserves the original event path through the shadow boundary.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current) return;
      // composedPath() returns the full event path including shadow DOM internals
      const path = e.composedPath();
      if (!path.includes(containerRef.current)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Determine if field has selection
  const hasSelection = () => {
    if (type === "multiselect" && Array.isArray(value)) {
      return value.length > 0;
    }
    if (type === "singleselect") {
      return typeof value === "string" && value !== "" && value !== "all";
    }
    if (type === "input") {
      return typeof value === "string" && value !== "";
    }
    return false;
  };

  // Handle multiselect toggle
  const handleMultiSelectToggle = (optionValue: string) => {
    if (!Array.isArray(value) || !onChange) return;

    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  // Handle single select
  const handleSingleSelect = (optionValue: string) => {
    if (!onChange) return;

    const newValue = value === optionValue ? "all" : optionValue;
    onChange(newValue);
    setIsOpen(false);
  };

  // Handle input change
  const handleInputChange = (inputValue: string) => {
    if (!onChange) return;
    onChange(inputValue);
  };

  // Days Open input with label and reset button
  if (type === "daysOpenSlider") {
    const stringValue = typeof value === "string" ? value : "";
    const hasValue = stringValue !== "";

    const handleDaysInputChange = (inputValue: string) => {
      if (!onChange) return;
      // Only allow positive integers
      const cleaned = inputValue.replace(/[^0-9]/g, "");
      onChange(cleaned);
    };

    const handleReset = () => {
      if (!onChange) return;
      onChange("");
    };

    return (
      <div className="space-y-2 w-full basis-full">
        <label className="text-sm font-medium text-[#3F4849]">{label}</label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min="0"
            placeholder="e.g. 30"
            value={stringValue}
            onChange={(e) => handleDaysInputChange(e.target.value)}
            className="h-10 border-[#E0E0E0] w-[120px]"
          />
          {/* Reset button */}
          {hasValue && (
            <button
              type="button"
              onClick={handleReset}
              className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
              title="Reset"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  if (type === "input") {
    return (
      <div className="space-y-2">
        {/* <label className="text-sm font-medium text-[#3F4849]">{label}</label> */}
        <Input
          type={inputType}
          placeholder={placeholder}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => handleInputChange(e.target.value)}
          className="h-10 border-[#E0E0E0]"
          min={min}
        />
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      {/* <label className="text-sm font-medium text-[#3F4849]">{label}</label> */}
      <FilterButton
        label={label}
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        hasSelection={hasSelection()}
      />

      {isOpen && (
        <DropdownContent width={width}>
          {type === "multiselect" && Array.isArray(value) && (
            <>
              {options.map((option) => (
                <MultiSelectOption
                  key={option.value}
                  label={option.label}
                  checked={value.includes(option.value)}
                  onChange={() => handleMultiSelectToggle(option.value)}
                />
              ))}
            </>
          )}

          {type === "singleselect" && (
            <>
              {options.map((option) => (
                <SingleSelectOption
                  key={option.value}
                  label={option.label}
                  selected={value === option.value}
                  onClick={() => handleSingleSelect(option.value)}
                />
              ))}
            </>
          )}
        </DropdownContent>
      )}
    </div>
  );
}
